import type { Lesson } from '../types';

export const lessons: Lesson[] = [
  // SĂPTĂMÂNA 1: Setup + Structură + Progres
  {
    id: 1,
    titlu: 'Ziua 1: Instalăm Uneltele',
    teorie: `Astăzi începem aventura noastră în dezvoltarea web! Pentru a construi aplicații moderne, avem nevoie de câteva unelte esențiale.

Node.js este un program care ne permite să rulăm JavaScript pe computer (nu doar în browser). npm (Node Package Manager) vine împreună cu Node.js și ne ajută să instalăm biblioteci create de alți programatori.

VS Code este editorul nostru de cod - locul unde vom scrie toate programele. Git ne ajută să salvăm versiuni ale codului și să colaborăm cu alții.

Gândește-te la aceste unelte ca la pensula, vopselele și pânza unui pictor - fără ele, nu putem crea!`,
    pasiConstruire: [
      'Descarcă și instalează Node.js de pe nodejs.org (versiunea LTS)',
      'Deschide terminalul și verifică instalarea: node --version',
      'Verifică și npm: npm --version',
      'Descarcă și instalează VS Code de pe code.visualstudio.com',
      'Instalează extensia "ES7+ React/Redux/React-Native snippets" în VS Code',
      'Instalează extensia "Prettier - Code formatter" pentru formatare automată',
      'Creează un folder pentru proiectele tale: mkdir proiecte-web'
    ],
    checklist: [
      'Node.js este instalat și funcționează',
      'VS Code este instalat cu extensiile necesare',
      'Pot deschide terminalul din VS Code',
      'Am creat folderul pentru proiecte'
    ],
    trofeulId: 'primul-pas',
    saptamana: 1,
    zi: 1
  },
  {
    id: 2,
    titlu: 'Ziua 2: Creăm Proiectul React',
    teorie: `Vite este un tool modern care ne ajută să creăm proiecte React rapid. Numele vine din franceză și înseamnă "rapid" - și chiar așa este!

React este o bibliotecă JavaScript creată de Facebook. Ne permite să construim interfețe utilizator din piese mici numite "componente". 

TypeScript este JavaScript cu superputeri - ne ajută să prindem erori înainte să rulăm codul, ca un corector ortografic pentru programatori.

Când rulăm comanda de creare, Vite pregătește tot ce avem nevoie: structura de foldere, configurări, și un server de dezvoltare care actualizează pagina instant când modificăm codul.`,
    pasiConstruire: [
      'Deschide terminalul în folderul de proiecte',
      'Rulează: npm create vite@latest camera-trofeelor -- --template react-ts',
      'Navighează în folder: cd camera-trofeelor',
      'Instalează dependențele: npm install',
      'Pornește serverul: npm run dev',
      'Deschide browserul la http://localhost:5173',
      'Deschide proiectul în VS Code: code .',
      'Explorează structura de foldere creată'
    ],
    checklist: [
      'Proiectul este creat cu succes',
      'npm install a rulat fără erori',
      'Serverul de dezvoltare pornește',
      'Pot vedea pagina în browser',
      'Înțeleg structura de bază a proiectului'
    ],
    trofeulId: 'arhitect-junior',
    saptamana: 1,
    zi: 2
  },
  {
    id: 3,
    titlu: 'Ziua 3: Prima Componentă React',
    teorie: `O componentă React este ca o cărămidă LEGO - o piesă reutilizabilă din care construim aplicația. Fiecare componentă are propriul cod și poate fi folosită de mai multe ori.

Componentele sunt funcții care returnează JSX - un fel de HTML scris în JavaScript. Arată așa: function Salut() { return <h1>Bună!</h1>; }

Props (proprietăți) sunt ca argumentele unei funcții - ne permit să trimitem date către componente. De exemplu, <Salut nume="Maria" /> trimite "Maria" ca prop.

State (stare) este memoria componentei - datele care se pot schimba în timp. Când state-ul se schimbă, React re-desenează componenta automat!`,
    pasiConstruire: [
      'Creează folderul src/components',
      'Creează fișierul src/components/Salut.tsx',
      'Scrie o componentă simplă care primește un prop "nume"',
      'Importă și folosește componenta în App.tsx',
      'Adaugă useState pentru a număra de câte ori s-a dat click pe un buton',
      'Stilizează componenta cu CSS inline sau className',
      'Creează o a doua componentă și folosește-o alături de prima'
    ],
    checklist: [
      'Am creat cel puțin 2 componente funcționale',
      'Componentele primesc și folosesc props',
      'Am folosit useState pentru stare locală',
      'Componentele sunt afișate corect în browser',
      'Înțeleg diferența între props și state'
    ],
    trofeulId: 'component-master',
    saptamana: 1,
    zi: 3
  },
  {
    id: 4,
    titlu: 'Ziua 4: Navigare cu React Router',
    teorie: `În aplicațiile web tradiționale, fiecare pagină este un fișier HTML separat. Când navighezi, browserul încarcă un fișier nou de pe server.

React Router ne permite să avem mai multe "pagini" într-o singură aplicație React (SPA - Single Page Application). Schimbarea paginii este instantă, fără re-încărcare!

Route definește ce componentă să afișăm pentru o anumită cale URL. De exemplu: /acasa arată HomePage, /trofee arată TrophyPage.

Link este ca un <a href> dar pentru React - navighează fără să reîncarce pagina. Folosim <Link to="/trofee">Trofee</Link> în loc de <a href="/trofee">.`,
    pasiConstruire: [
      'Instalează react-router-dom: npm install react-router-dom',
      'Creează folderul src/pages cu fișiere pentru HomePage, TrophyPage',
      'În main.tsx, învelește App cu BrowserRouter',
      'În App.tsx, configurează Routes cu Route pentru fiecare pagină',
      'Creează un component Navigation cu Link-uri',
      'Adaugă Navigation în App pentru a apărea pe toate paginile',
      'Testează navigarea între pagini în browser'
    ],
    checklist: [
      'React Router este instalat',
      'Am cel puțin 3 pagini diferite',
      'Navigarea funcționează cu Link',
      'URL-ul se schimbă când navighez',
      'Pagina nu se reîncarcă la navigare'
    ],
    trofeulId: 'navigator',
    saptamana: 1,
    zi: 4
  },
  {
    id: 5,
    titlu: 'Ziua 5: Salvăm Progresul',
    teorie: `localStorage este o "cutie" în browser unde putem salva date care persistă chiar după închiderea browserului. Fiecare site are propriul localStorage.

Datele se salvează ca text (string), deci obiectele trebuie convertite cu JSON.stringify() și citite înapoi cu JSON.parse().

Este perfect pentru salvarea progresului: lecții completate, setări utilizator, trofee deblocate. Nu necesită server sau bază de date!

Atenție: localStorage are o limită de ~5MB și nu este sigur pentru date sensibile (parole, token-uri). Pentru proiectul nostru este ideal!`,
    pasiConstruire: [
      'Creează fișierul src/lib/storage.ts',
      'Definește interfața Progress cu tipurile necesare',
      'Implementează getProgress() care citește din localStorage',
      'Implementează saveProgress() care salvează în localStorage',
      'Adaugă valori default pentru progres nou',
      'Creează un hook useProgress() pentru acces ușor din componente',
      'Testează salvarea și citirea în consola browserului',
      'Verifică în DevTools > Application > Local Storage'
    ],
    checklist: [
      'Funcțiile de storage sunt implementate',
      'Datele persistă după refresh',
      'Am testat în DevTools',
      'Hook-ul useProgress funcționează',
      'Erorile sunt tratate (try/catch)'
    ],
    trofeulId: 'salvator-progres',
    saptamana: 1,
    zi: 5
  },

  // SĂPTĂMÂNA 2: CSS + Grid + Accesibilitate
  {
    id: 6,
    titlu: 'Ziua 6: CSS Variabile și Teme',
    teorie: `CSS Variables (custom properties) sunt ca variabilele din programare - stochează valori pe care le putem refolosi. Se declară cu -- și se folosesc cu var().

Declarăm variabilele în :root pentru a fi disponibile global: :root { --culoare-primara: #3498db; }. Apoi le folosim: background: var(--culoare-primara);

Avantajul major: schimbând o singură variabilă, actualizăm toate elementele care o folosesc. Perfect pentru teme (dark/light mode)!

Pentru tema întunecată, adăugăm o clasă pe body și redefinim variabilele: body.dark { --culoare-primara: #2980b9; }`,
    pasiConstruire: [
      'Creează src/styles/variables.css cu variabilele de bază',
      'Definește culori pentru tema light: --bg, --text, --primary, --secondary',
      'Adaugă variabile pentru spațiere: --spacing-sm, --spacing-md, --spacing-lg',
      'Creează clasa .dark cu culorile pentru tema întunecată',
      'Importă variables.css în main.tsx',
      'Adaugă un buton de schimbare temă în Navigation',
      'Salvează preferința temei în localStorage'
    ],
    checklist: [
      'Variabilele CSS sunt definite în :root',
      'Toate culorile folosesc variabile',
      'Tema dark funcționează',
      'Preferința se salvează în localStorage',
      'Tranziția între teme este smooth'
    ],
    trofeulId: 'stilist',
    saptamana: 2,
    zi: 1
  },
  {
    id: 7,
    titlu: 'Ziua 7: CSS Grid pentru Layout',
    teorie: `CSS Grid este cel mai puternic sistem de layout din CSS. Ne permite să creăm grile 2D cu rânduri și coloane, controlând exact unde se poziționează elementele.

Activăm grid cu display: grid pe container. Apoi definim coloanele: grid-template-columns: repeat(3, 1fr) creează 3 coloane egale.

fr este o unitate specială care înseamnă "fracțiune din spațiul disponibil". 1fr 2fr înseamnă că a doua coloană e de 2 ori mai lată.

gap controlează spațiul între celule. grid-column și grid-row permit unui element să ocupe mai multe celule.`,
    pasiConstruire: [
      'Creează un layout de bază pentru TrophyRoom cu Grid',
      'Folosește grid-template-columns: repeat(auto-fill, minmax(200px, 1fr))',
      'Adaugă gap pentru spațiere între carduri',
      'Fă primul card să ocupe 2 coloane cu grid-column: span 2',
      'Adaugă grid-template-areas pentru layout-ul paginii principale',
      'Creează un header care ocupă toată lățimea',
      'Testează pe diferite dimensiuni de ecran'
    ],
    checklist: [
      'Layout-ul folosește CSS Grid',
      'Trofeele sunt afișate într-o grilă responsivă',
      'Gap-ul este consistent',
      'Am folosit auto-fill sau auto-fit',
      'Layout-ul arată bine pe mobile'
    ],
    trofeulId: 'grid-guru',
    saptamana: 2,
    zi: 2
  },
  {
    id: 8,
    titlu: 'Ziua 8: Flexbox pentru Componente',
    teorie: `Flexbox este ideal pentru layout-uri 1D (un rând sau o coloană). Îl folosim pentru navigare, carduri, butoane aliniate.

display: flex activează Flexbox. justify-content aliniază pe axa principală (orizontal), align-items pe axa secundară (vertical).

flex-direction schimbă direcția: row (default) sau column. flex-wrap: wrap permite elementelor să treacă pe rând nou.

Proprietățile pe copii: flex-grow (cât crește), flex-shrink (cât se micșorează), flex-basis (dimensiune de bază). Shorthand: flex: 1 înseamnă flex: 1 1 0.`,
    pasiConstruire: [
      'Refă navigarea cu Flexbox: display: flex, justify-content: space-between',
      'Creează un card component cu header (flex) și content',
      'Folosește align-items: center pentru a centra vertical',
      'Creează un footer sticky cu Flexbox',
      'Folosește flex-wrap pentru butoane care se adaptează',
      'Combină Flexbox (componente) cu Grid (layout pagină)',
      'Adaugă gap în loc de margin pentru spațiere'
    ],
    checklist: [
      'Navigarea folosește Flexbox',
      'Cardurile au layout Flexbox intern',
      'Înțeleg diferența dintre Grid și Flexbox',
      'Folosesc gap în loc de margin',
      'Componentele se adaptează la conținut'
    ],
    trofeulId: 'flex-master',
    saptamana: 2,
    zi: 3
  },
  {
    id: 9,
    titlu: 'Ziua 9: Accesibilitate Web',
    teorie: `Accesibilitatea (a11y) înseamnă că site-ul poate fi folosit de toți, inclusiv persoanele cu dizabilități. Nu e doar etică - e și lege în multe țări!

Semantic HTML: folosește <button> pentru acțiuni, <nav> pentru navigare, <main> pentru conținut principal. Screen reader-ele înțeleg aceste tag-uri.

ARIA (Accessible Rich Internet Applications) adaugă informații pentru tehnologiile asistive: aria-label, aria-hidden, role.

Focus management: elementele interactive trebuie să fie accesibile cu Tab. :focus-visible arată un outline clar.`,
    pasiConstruire: [
      'Înlocuiește div-urile cu tag-uri semantice: nav, main, section, article',
      'Adaugă aria-label pe butoanele cu doar iconițe',
      'Asigură contrast suficient între text și fundal (minim 4.5:1)',
      'Adaugă :focus-visible cu outline vizibil pe elemente interactive',
      'Testează navigarea doar cu tastatura (Tab, Enter, Escape)',
      'Adaugă skip link pentru a sări la conținutul principal',
      'Verifică cu extensia Lighthouse din Chrome DevTools'
    ],
    checklist: [
      'Folosesc tag-uri HTML semantice',
      'Butoanele au text sau aria-label',
      'Pot naviga complet cu tastatura',
      'Contrastul culorilor este suficient',
      'Lighthouse Accessibility score > 90'
    ],
    trofeulId: 'accesibilitate-erou',
    saptamana: 2,
    zi: 4
  },
  {
    id: 10,
    titlu: 'Ziua 10: Design Responsive',
    teorie: `Design responsive înseamnă că site-ul se adaptează la orice dimensiune de ecran: telefon, tabletă, desktop, televizor.

Media queries ne permit să aplicăm stiluri diferite în funcție de dimensiune: @media (max-width: 768px) { ... } pentru mobile.

Mobile-first: scriem CSS-ul pentru mobile mai întâi, apoi adăugăm stiluri pentru ecrane mari cu min-width. E mai eficient!

Unități relative: folosim rem, em, %, vw, vh în loc de px. Imaginile au max-width: 100% pentru a nu depăși containerul.`,
    pasiConstruire: [
      'Adaugă meta viewport în index.html (deja există de la Vite)',
      'Rescrie CSS-ul mobile-first: stiluri de bază pentru mobile',
      'Adaugă breakpoint pentru tabletă: @media (min-width: 768px)',
      'Adaugă breakpoint pentru desktop: @media (min-width: 1024px)',
      'Folosește clamp() pentru font-size responsive: clamp(1rem, 2vw, 1.5rem)',
      'Testează în Chrome DevTools cu device toolbar',
      'Verifică pe un telefon real sau emulator'
    ],
    checklist: [
      'Site-ul arată bine pe mobile (320px)',
      'Grid-ul se adaptează la dimensiune',
      'Textul este lizibil fără zoom',
      'Butoanele sunt suficient de mari pentru touch',
      'Nu există scroll orizontal nedorit'
    ],
    trofeulId: 'responsive-ninja',
    saptamana: 2,
    zi: 5
  },

  // SĂPTĂMÂNA 3: Logică + Filtre + Teste
  {
    id: 11,
    titlu: 'Ziua 11: Logică cu Condiții',
    teorie: `Condițiile ne permit să luăm decizii în cod. În React, folosim expresii pentru a afișa conținut diferit în funcție de state.

Operatorul ternary: {isUnlocked ? <TrophyCard /> : <LockedCard />} afișează un card sau altul.

Operatorul && pentru afișare condiționată: {score > 100 && <Badge />} afișează Badge doar dacă scorul e peste 100.

Pentru condiții complexe, folosim funcții sau variabile: const buttonText = isComplete ? "Continuă" : "Începe"; apoi {buttonText}.`,
    pasiConstruire: [
      'Creează un TrophyCard care arată diferit dacă e locked/unlocked',
      'Folosește operatorul ternary pentru a schimba iconița',
      'Adaugă un badge "NOU!" pentru trofee deblocate recent',
      'Creează un mesaj de bun venit personalizat în funcție de progres',
      'Implementează logica pentru "Ziua recomandată" pe HomePage',
      'Adaugă clase CSS condiționale cu template literals',
      'Testează toate ramurile condițiilor'
    ],
    checklist: [
      'TrophyCard arată diferit pentru locked/unlocked',
      'Am folosit ternary și && corect',
      'Clasele CSS se aplică condiționat',
      'Logica pentru ziua recomandată funcționează',
      'Toate condițiile sunt testate'
    ],
    trofeulId: 'logician',
    saptamana: 3,
    zi: 1
  },
  {
    id: 12,
    titlu: 'Ziua 12: Filtre și Căutare',
    teorie: `Filtrarea și căutarea sunt esențiale pentru orice aplicație cu multe date. În React, le implementăm cu state și metode de array.

.filter() returnează un array nou cu elementele care îndeplinesc condiția: trophies.filter(t => t.category === 'web').

.includes() verifică dacă un string conține alt string: trophy.name.toLowerCase().includes(searchTerm.toLowerCase()).

Combinăm mai multe filtre cu && sau creăm funcții de filtrare separate pe care le aplicăm în lanț.`,
    pasiConstruire: [
      'Adaugă un input de căutare în TrophyRoom',
      'Creează state pentru searchTerm cu useState',
      'Filtrează trofeele în funcție de searchTerm',
      'Adaugă butoane pentru filtrare pe categorii (web/game/ai/bonus)',
      'Creează state pentru categoria selectată',
      'Combină filtrul de căutare cu filtrul de categorie',
      'Afișează mesaj când nu sunt rezultate',
      'Adaugă un buton de "Resetează filtre"'
    ],
    checklist: [
      'Căutarea funcționează (case-insensitive)',
      'Filtrele pe categorii funcționează',
      'Filtrele se pot combina',
      'Există mesaj pentru 0 rezultate',
      'Butonul de reset funcționează'
    ],
    trofeulId: 'filtru-expert',
    saptamana: 3,
    zi: 2
  },
  {
    id: 13,
    titlu: 'Ziua 13: Primul Test',
    teorie: `Testele automate verifică că codul funcționează corect. Când facem modificări, rulăm testele pentru a ne asigura că nu am stricat nimic.

Vitest este framework-ul de testare pentru Vite. Sintaxa: describe grupează teste, it sau test definește un test individual, expect face verificarea.

Pentru componente React, folosim @testing-library/react: render montează componenta, screen găsește elemente, userEvent simulează interacțiuni.

Un test bun: testează comportamentul, nu implementarea. Întreabă "ce ar trebui să vadă utilizatorul?" nu "ce funcție se apelează?".`,
    pasiConstruire: [
      'Instalează: npm install -D vitest @testing-library/react @testing-library/jest-dom',
      'Adaugă "test": "vitest" în package.json scripts',
      'Creează src/components/TrophyCard.test.tsx',
      'Scrie un test că TrophyCard afișează numele trofeului',
      'Scrie un test că TrophyCard locked arată lacătul',
      'Scrie un test pentru funcția de căutare din TrophyRoom',
      'Rulează testele: npm test',
      'Verifică că toate testele trec'
    ],
    checklist: [
      'Vitest este configurat corect',
      'Am cel puțin 3 teste care trec',
      'Testez componente cu @testing-library',
      'Înțeleg describe, it, expect',
      'npm test rulează fără erori'
    ],
    trofeulId: 'tester',
    saptamana: 3,
    zi: 3
  },
  {
    id: 14,
    titlu: 'Ziua 14: Debugging',
    teorie: `Debugging înseamnă găsirea și repararea bug-urilor. Chrome DevTools este cel mai puternic instrument pentru debugging în browser.

Console: console.log() pentru valori, console.table() pentru array-uri, console.error() pentru erori. Dar nu uita să le ștergi din producție!

Breakpoints: în tab-ul Sources, click pe numărul liniei pentru a pune un breakpoint. Codul se oprește acolo și poți inspecta variabilele.

React DevTools (extensie Chrome) arată structura componentelor, props, state, și permite modificarea valorilor live.`,
    pasiConstruire: [
      'Instalează extensia React DevTools în Chrome',
      'Deschide DevTools și explorează tab-ul Components',
      'Modifică un state din DevTools și vezi efectul',
      'Pune un breakpoint în handleClick al unui buton',
      'Folosește step over și step into pentru a urmări execuția',
      'Găsește și repară un bug intenționat (pregătit de tine)',
      'Folosește console.table pentru a afișa array-ul de trofee',
      'Curăță console.log-urile din cod'
    ],
    checklist: [
      'React DevTools este instalat și funcțional',
      'Pot pune și folosi breakpoints',
      'Înțeleg step over vs step into',
      'Am reparat cel puțin un bug',
      'Codul nu mai are console.log-uri inutile'
    ],
    trofeulId: 'bug-hunter',
    saptamana: 3,
    zi: 4
  },
  {
    id: 15,
    titlu: 'Ziua 15: State Management Avansat',
    teorie: `Pe măsură ce aplicația crește, gestionarea state-ului devine complexă. Când mai multe componente au nevoie de aceleași date, avem opțiuni.

Lifting state up: mutăm state-ul în componenta părinte comună și îl trimitem ca props. Funcționează, dar poate deveni complicat (prop drilling).

Context API: React Context ne permite să "teleportăm" date în orice componentă, fără a le pasa prin fiecare nivel. Ideal pentru teme, utilizator autentificat, sau în cazul nostru - progresul.

Custom hooks: extragem logică reutilizabilă în hook-uri proprii. useProgress() poate fi folosit în orice componentă.`,
    pasiConstruire: [
      'Creează src/context/ProgressContext.tsx',
      'Definește ProgressContext cu createContext',
      'Creează ProgressProvider care ține state-ul și funcțiile',
      'Învelește App cu ProgressProvider în main.tsx',
      'Creează hook-ul useProgressContext pentru acces ușor',
      'Refactorizează componentele să folosească contextul',
      'Adaugă funcții: completeLesson, unlockTrophy în context',
      'Testează că state-ul se sincronizează între pagini'
    ],
    checklist: [
      'ProgressContext este creat corect',
      'Provider învelește aplicația',
      'Hook-ul personalizat funcționează',
      'State-ul e accesibil din orice componentă',
      'Funcțiile de update sunt în context'
    ],
    trofeulId: 'state-manager',
    saptamana: 3,
    zi: 5
  },

  // SĂPTĂMÂNA 4: Mini-joc
  {
    id: 16,
    titlu: 'Ziua 16: Introducere în Canvas',
    teorie: `Canvas este un element HTML care permite desenarea 2D (sau 3D cu WebGL). Îl folosim pentru jocuri, grafice, animații.

Obținem contextul de desenare cu canvas.getContext('2d'). Apoi avem metode: fillRect, strokeRect, arc (cerc), beginPath, moveTo, lineTo.

Culori: fillStyle pentru umplere, strokeStyle pentru contur. Coordonatele încep din colțul stânga-sus (0,0).

În React, folosim useRef pentru a accesa elementul canvas și useEffect pentru a desena după ce componenta e montată.`,
    pasiConstruire: [
      'Creează src/pages/GamePage.tsx cu un element canvas',
      'Folosește useRef<HTMLCanvasElement> pentru referință',
      'În useEffect, obține contextul 2D',
      'Desenează un fundal colorat cu fillRect',
      'Desenează un cerc (jucătorul) cu arc()',
      'Desenează câteva dreptunghiuri (monede)',
      'Adaugă text cu fillText pentru scor',
      'Experimentează cu culori și forme diferite'
    ],
    checklist: [
      'Canvas se afișează corect',
      'Am desenat cel puțin 3 forme diferite',
      'Înțeleg sistemul de coordonate',
      'Pot schimba culorile',
      'useRef și useEffect sunt folosite corect'
    ],
    trofeulId: 'game-dev',
    saptamana: 4,
    zi: 1
  },
  {
    id: 17,
    titlu: 'Ziua 17: Animație cu requestAnimationFrame',
    teorie: `Pentru animații smooth (60 FPS), folosim requestAnimationFrame în loc de setInterval. Browserul optimizează și sincronizează cu refresh rate-ul ecranului.

Game loop-ul: 1) Actualizează state-ul jocului (poziții, coliziuni), 2) Șterge canvas-ul, 3) Redesenează totul, 4) Cere următorul frame.

Pattern: function gameLoop() { update(); render(); requestAnimationFrame(gameLoop); }

Curățare: în return-ul useEffect, folosim cancelAnimationFrame pentru a opri animația când componenta e demontată.`,
    pasiConstruire: [
      'Creează state pentru poziția jucătorului: { x, y }',
      'Creează funcția gameLoop cu update și render',
      'În update, mișcă jucătorul automat (pentru test)',
      'În render, șterge și redesenează canvas-ul',
      'Folosește requestAnimationFrame în useEffect',
      'Adaugă cleanup cu cancelAnimationFrame',
      'Adaugă un state pentru a porni/opri jocul',
      'Măsoară FPS-ul cu performance.now()'
    ],
    checklist: [
      'Animația rulează smooth',
      'Jucătorul se mișcă pe ecran',
      'Game loop-ul are update și render',
      'Cleanup-ul funcționează corect',
      'Nu există memory leaks'
    ],
    trofeulId: 'coin-collector',
    saptamana: 4,
    zi: 2
  },
  {
    id: 18,
    titlu: 'Ziua 18: Controale de la Tastatură',
    teorie: `Pentru jocuri, ascultăm evenimentele keydown și keyup. Stocăm tastele apăsate într-un Set sau obiect pentru a permite mișcare diagonală.

WASD și săgeți: ne interesează w/ArrowUp, s/ArrowDown, a/ArrowLeft, d/ArrowRight.

Pattern: la keydown adăugăm tasta în Set, la keyup o scoatem. În update verificăm ce taste sunt apăsate și mișcăm jucătorul.

event.preventDefault() pe ArrowUp/Down oprește scroll-ul paginii când jucăm.`,
    pasiConstruire: [
      'Creează un Set pentru tastele apăsate: useRef(new Set())',
      'Adaugă event listener pentru keydown în useEffect',
      'Adaugă event listener pentru keyup',
      'La keydown, adaugă event.key în Set',
      'La keyup, șterge event.key din Set',
      'În update, verifică tastele și mișcă jucătorul',
      'Adaugă preventDefault pentru săgeți',
      'Limitează jucătorul să nu iasă din canvas'
    ],
    checklist: [
      'Jucătorul se mișcă cu WASD',
      'Jucătorul se mișcă și cu săgețile',
      'Mișcarea diagonală funcționează',
      'Jucătorul nu iese din canvas',
      'Pagina nu face scroll la săgeți'
    ],
    trofeulId: 'speed-runner',
    saptamana: 4,
    zi: 3
  },
  {
    id: 19,
    titlu: 'Ziua 19: Coliziuni și Scor',
    teorie: `Detecția coliziunilor verifică dacă două obiecte se suprapun. Pentru dreptunghiuri, verificăm dacă se intersectează pe ambele axe.

AABB (Axis-Aligned Bounding Box): dacă x1 < x2+w2 && x1+w1 > x2 && y1 < y2+h2 && y1+h1 > y2, avem coliziune.

Pentru cercuri: calculăm distanța între centre și comparăm cu suma razelor. dist = sqrt((x1-x2)² + (y1-y2)²).

La coliziune cu moneda: incrementăm scorul, eliminăm moneda, eventual generăm alta în altă poziție.`,
    pasiConstruire: [
      'Creează un array de monede cu poziții random',
      'Implementează funcția checkCollision pentru AABB',
      'În update, verifică coliziunea jucător-monede',
      'La coliziune, incrementează scorul și elimină moneda',
      'Adaugă noi monede când una e colectată',
      'Afișează scorul pe canvas cu fillText',
      'Adaugă sunet sau efect vizual la colectare',
      'Testează coliziunile din diferite unghiuri'
    ],
    checklist: [
      'Coliziunile sunt detectate corect',
      'Scorul crește la colectare',
      'Monedele colectate dispar',
      'Apar monede noi',
      'Scorul e afișat vizibil'
    ],
    trofeulId: 'arcade-champ',
    saptamana: 4,
    zi: 4
  },
  {
    id: 20,
    titlu: 'Ziua 20: Timer și Game Over',
    teorie: `Un timer de joc: decrementăm un contor la fiecare secundă sau frame. Când ajunge la 0, jocul se termină.

Pentru timer în secunde: folosim setInterval sau calculăm timpul trecut cu Date.now() sau performance.now().

Game over screen: afișăm scorul final, buton de restart, eventual salvăm high score în localStorage.

Deblocăm trofeul "Arcade Champ" dacă scorul >= pragul definit la sfârșitul jocului.`,
    pasiConstruire: [
      'Adaugă state pentru timer (30 secunde)',
      'Decrementează timer-ul în fiecare secundă',
      'Când timer = 0, oprește game loop-ul',
      'Afișează timer-ul pe canvas',
      'Creează ecranul de Game Over',
      'Arată scorul final și high score',
      'Adaugă buton Restart care resetează jocul',
      'Verifică și deblochează trofeul dacă scor >= 50',
      'Salvează high score în localStorage'
    ],
    checklist: [
      'Timer-ul numără corect',
      'Jocul se oprește când timer = 0',
      'Ecran Game Over cu scor',
      'Restart funcționează',
      'Trofeul se deblochează la scor suficient',
      'High score se salvează'
    ],
    trofeulId: 'no-damage',
    saptamana: 4,
    zi: 5
  },

  // SĂPTĂMÂNA 5: AI Generator + Securizare
  {
    id: 21,
    titlu: 'Ziua 21: Formular AI Generator',
    teorie: `Formularul pentru generarea trofeelor cu AI are 3 câmpuri: titlu, temă, stil. Utilizatorul completează și apasă "Generează".

Controlled components: valorile input-urilor sunt controlate de state React. La onChange actualizăm state-ul, value afișează state-ul.

Validare: verificăm că toate câmpurile sunt completate înainte de submit. Afișăm erori inline sau într-un toast.

Loading state: când așteptăm răspunsul de la server, afișăm un spinner și dezactivăm butonul.`,
    pasiConstruire: [
      'Creează src/pages/AIGeneratorPage.tsx',
      'Adaugă 3 input-uri controlled: title, theme, style',
      'Creează state pentru formData și pentru loading',
      'Adaugă validare: toate câmpurile obligatorii',
      'Afișează mesaje de eroare pentru câmpuri goale',
      'Creează funcția handleSubmit (deocamdată console.log)',
      'Adaugă spinner când loading = true',
      'Stilizează formularul să arate bine'
    ],
    checklist: [
      'Formularul are 3 câmpuri controlled',
      'Validarea funcționează',
      'Erorile se afișează clar',
      'Loading state dezactivează formularul',
      'Submit-ul loghează datele în consolă'
    ],
    trofeulId: 'ai-explorer',
    saptamana: 5,
    zi: 1
  },
  {
    id: 22,
    titlu: 'Ziua 22: API Endpoint Serverless',
    teorie: `Nu punem chei API în frontend pentru că oricine poate vedea codul JavaScript. Folosim un server sau funcții serverless care țin cheile în siguranță.

Cloudflare Workers/Pages Functions sunt funcții serverless care rulează pe edge. Primesc cererea, fac apelul la API-ul AI, și returnează rezultatul.

Flow: Frontend → Serverless Function → OpenAI/alt provider → Serverless Function → Frontend.

În funcție verificăm: rate limiting, validare input, gestionare erori. Dacă nu există cheie API, returnăm un placeholder.`,
    pasiConstruire: [
      'Creează folderul functions/api (pentru Cloudflare Pages)',
      'Creează functions/api/generate-trophy.ts',
      'Implementează handler-ul care primește POST cu JSON',
      'Validează input-ul: title, theme, style',
      'Adaugă rate limiting simplu (în-memory pentru dev)',
      'Dacă nu există API key, returnează placeholder SVG',
      'Dacă există, apelează OpenAI sau alt provider',
      'Returnează { trophyName, trophyDescription, imageDataUrl }'
    ],
    checklist: [
      'Endpoint-ul răspunde la POST',
      'Input-ul este validat',
      'Modul NO_API returnează placeholder',
      'Rate limiting funcționează',
      'Erori sunt returnate cu status corespunzător'
    ],
    trofeulId: 'prompt-master',
    saptamana: 5,
    zi: 2
  },
  {
    id: 23,
    titlu: 'Ziua 23: Apel API din Frontend',
    teorie: `fetch() este API-ul browser-ului pentru cereri HTTP. Returnează un Promise, deci folosim async/await.

Pentru POST cu JSON: fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).

Gestionare răspuns: verificăm response.ok, apoi parsăm cu response.json(). Dacă eroare, aruncăm/prindem.

AbortController ne permite să anulăm cererea: const controller = new AbortController(); fetch(url, { signal: controller.signal }).`,
    pasiConstruire: [
      'În AIGeneratorPage, creează funcția generateTrophy',
      'Folosește fetch pentru POST la /api/generate-trophy',
      'Trimite { title, theme, style } în body',
      'Gestionează răspunsul și actualizează state-ul',
      'Afișează trofeul generat: nume, descriere, imagine',
      'Implementează AbortController pentru cancel',
      'Adaugă buton Cancel vizibil în timpul loading',
      'Tratează erorile și afișează mesaj utilizatorului'
    ],
    checklist: [
      'fetch apelează endpoint-ul corect',
      'Datele sunt trimise ca JSON',
      'Răspunsul e parsat și afișat',
      'Cancel funcționează',
      'Erorile sunt afișate prietenos'
    ],
    trofeulId: 'securitate-expert',
    saptamana: 5,
    zi: 3
  },
  {
    id: 24,
    titlu: 'Ziua 24: Salvare Trofee AI',
    teorie: `IndexedDB este o bază de date în browser, mai puternică decât localStorage. Poate stoca cantități mari de date, inclusiv blob-uri (imagini).

Folosim librăria "idb" pentru o interfață mai simplă cu Promises în loc de callbacks.

Schema: cream un object store "ai-trophies" cu un index pe id. Stocăm: id, name, description, imageDataUrl, createdAt, prompt.

Operații CRUD: add, get, getAll, put, delete. Toate sunt async.`,
    pasiConstruire: [
      'Instalează librăria idb: npm install idb',
      'Creează src/lib/trophyDB.ts',
      'Definește schema bazei de date cu object store',
      'Implementează saveAITrophy(trophy)',
      'Implementează getAITrophies() pentru lista completă',
      'Implementează deleteAITrophy(id)',
      'După generare, salvează trofeul în IndexedDB',
      'În TrophyRoom, afișează și trofeele AI',
      'Adaugă opțiune de ștergere pentru trofeele AI'
    ],
    checklist: [
      'IndexedDB este configurată corect',
      'Trofeele AI se salvează',
      'Lista de trofee AI se încarcă',
      'Ștergerea funcționează',
      'Trofeele AI apar în TrophyRoom'
    ],
    trofeulId: 'data-guardian',
    saptamana: 5,
    zi: 4
  },
  {
    id: 25,
    titlu: 'Ziua 25: Caching și Rate Limiting',
    teorie: `Rate limiting previne abuzul API-ului: limitează câte cereri poate face un utilizator într-un interval de timp.

În frontend, ținem un contor în localStorage cu timestamp-uri. Dacă > 5 cereri în ultimul minut, blocăm.

Caching: salvăm răspunsurile API în funcție de hash-ul prompt-ului. Dacă același prompt, returnăm din cache.

Hash simplu: convertim string-ul în număr sau folosim o funcție de hash. Cache-ul poate fi în localStorage sau IndexedDB.`,
    pasiConstruire: [
      'Creează src/lib/rateLimit.ts cu funcții de verificare',
      'Implementează canMakeRequest() și recordRequest()',
      'Înainte de fetch, verifică rate limit',
      'Afișează mesaj dacă limita e atinsă',
      'Creează funcție simplă de hash pentru prompt',
      'Verifică cache-ul înainte de a face cererea',
      'Salvează răspunsurile în cache după primire',
      'Adaugă buton "Generează din nou" care ignoră cache-ul'
    ],
    checklist: [
      'Rate limiting blochează după 5 cereri/minut',
      'Mesaj clar când e blocat',
      'Cache-ul funcționează pentru prompt identic',
      'Opțiune de a ignora cache-ul',
      'Nu se fac cereri inutile la API'
    ],
    trofeulId: 'colectionar',
    saptamana: 5,
    zi: 5
  },

  // SĂPTĂMÂNA 6: Export/Import + Deploy + Demo
  {
    id: 26,
    titlu: 'Ziua 26: Export Progres',
    teorie: `Exportul permite utilizatorului să salveze o copie de backup a progresului. Creăm un fișier JSON descărcabil.

Blob API: new Blob([jsonString], { type: 'application/json' }) creează un obiect descărcabil.

Download programatic: creăm un link temporar cu URL.createObjectURL(blob), setăm download și href, simulăm click.

Includem în export: progres lecții, trofee deblocate, setări, data exportului. Versiune schema pentru compatibilitate.`,
    pasiConstruire: [
      'Creează funcția exportProgress() în storage.ts',
      'Colectează toate datele: progres, trofee, setări',
      'Adaugă metadata: versiune, data export',
      'Convertește în JSON string formatat',
      'Creează Blob și URL temporar',
      'Generează nume fișier cu data: camera-trofeelor-2024-01-15.json',
      'Declanșează download-ul',
      'Curăță URL-ul temporar după download'
    ],
    checklist: [
      'Butonul Export descarcă un fișier JSON',
      'Fișierul conține toate datele necesare',
      'Numele include data curentă',
      'JSON-ul este formatat (readable)',
      'Versiunea schema este inclusă'
    ],
    trofeulId: 'early-bird',
    saptamana: 6,
    zi: 1
  },
  {
    id: 27,
    titlu: 'Ziua 27: Import și Validare',
    teorie: `Importul citește un fișier JSON și restaurează progresul. Trebuie să validăm datele pentru a evita coruperea aplicației.

FileReader API: citește fișiere selectate de utilizator. reader.readAsText(file) și reader.onload pentru rezultat.

Validare schema: verificăm că JSON-ul are structura așteptată. Proprietăți obligatorii, tipuri corecte, versiune compatibilă.

UX: cerem confirmare înainte de a suprascrie, afișăm previzualizare a ce se va importa.`,
    pasiConstruire: [
      'Adaugă input type="file" accept=".json" în Settings',
      'Creează handler pentru onChange cu FileReader',
      'Parsează JSON-ul și validează structura',
      'Verifică versiunea schema și migrează dacă e nevoie',
      'Afișează dialog de confirmare cu sumar',
      'La confirmare, salvează datele în storage',
      'Afișează succes sau erori specifice',
      'Reîncarcă pagina sau actualizează context-ul'
    ],
    checklist: [
      'Pot selecta un fișier JSON',
      'JSON invalid arată eroare clară',
      'Structură incorectă e detectată',
      'Dialog de confirmare apare',
      'Progresul e restaurat corect'
    ],
    trofeulId: 'perseverent',
    saptamana: 6,
    zi: 2
  },
  {
    id: 28,
    titlu: 'Ziua 28: Build și Optimizare',
    teorie: `Build-ul de producție optimizează aplicația: minifică codul, elimină cod nefolosit (tree shaking), split-uiește în chunk-uri.

npm run build creează folderul dist/ cu fișierele optimizate. Preview cu npm run preview.

Optimizări: lazy loading pentru pagini (React.lazy), imagini optimizate, fonts preload, PWA pentru offline.

Verificări: bundle size (< 500KB ideal), Lighthouse scores, testare pe dispozitive reale.`,
    pasiConstruire: [
      'Rulează npm run build și verifică output-ul',
      'Analizează bundle size cu vite-plugin-visualizer (optional)',
      'Implementează lazy loading pentru pagini cu React.lazy',
      'Adaugă Suspense cu loading fallback',
      'Optimizează imaginile (format, dimensiune)',
      'Adaugă meta tags în index.html pentru SEO',
      'Rulează npm run preview și testează',
      'Verifică scorurile Lighthouse'
    ],
    checklist: [
      'Build-ul rulează fără erori',
      'dist/ conține fișierele',
      'Lazy loading funcționează',
      'Bundle size e rezonabil',
      'Lighthouse Performance > 80'
    ],
    trofeulId: 'deployer',
    saptamana: 6,
    zi: 3
  },
  {
    id: 29,
    titlu: 'Ziua 29: Deploy pe Cloudflare',
    teorie: `Cloudflare Pages oferă hosting gratuit cu CDN global, HTTPS automat, și suport pentru funcții serverless.

Conectăm repo-ul GitHub la Cloudflare Pages. La fiecare push pe main, se face build și deploy automat.

Configurare: build command "npm run build", output directory "dist", variabile de mediu pentru API keys.

Pages Functions: fișierele din /functions devin endpoint-uri serverless automat.`,
    pasiConstruire: [
      'Creează cont Cloudflare (gratuit)',
      'Push codul pe GitHub (dacă nu e deja)',
      'În Cloudflare Dashboard, creează proiect Pages',
      'Conectează repo-ul GitHub',
      'Configurează: build command, output dir',
      'Adaugă variabilele de mediu (API keys) în Settings',
      'Trigger build-ul și așteaptă',
      'Testează site-ul la URL-ul generat',
      'Verifică că endpoint-ul /api/generate-trophy funcționează'
    ],
    checklist: [
      'Site-ul e live pe Cloudflare Pages',
      'HTTPS funcționează',
      'Toate paginile se încarcă',
      'API endpoint-ul răspunde',
      'Nu sunt erori în consolă'
    ],
    trofeulId: 'absolvent',
    saptamana: 6,
    zi: 4
  },
  {
    id: 30,
    titlu: 'Ziua 30: Demo și Celebrare',
    teorie: `Felicitări! Ai parcurs 30 de zile și ai construit o aplicație completă. E timpul să faci demo și să celebrezi!

Demo: prezintă aplicația familiei sau prietenilor. Arată toate funcționalitățile: lecții, trofee, jocul, generatorul AI.

Reflecție: ce ai învățat? Ce a fost dificil? Ce vrei să explorezi în continuare?

Următorii pași: adaugă features noi, învață alte tehnologii, construiește alt proiect, contribuie la open source.`,
    pasiConstruire: [
      'Fă o listă cu toate feature-urile aplicației',
      'Pregătește o prezentare scurtă (5 minute)',
      'Fă demo live familiei sau prietenilor',
      'Arată codul și explică câteva bucăți cheie',
      'Colectează feedback și notează ideile',
      'Scrie în README ce ai învățat',
      'Postează proiectul pe social media (optional)',
      'Celebrează! Ai meritat!'
    ],
    checklist: [
      'Am făcut demo aplicației',
      'Am explicat cum funcționează',
      'Am primit feedback',
      'README-ul e complet',
      'Toate cele 30 de trofee sunt deblocate! 🏆'
    ],
    trofeulId: 'absolvent',
    saptamana: 6,
    zi: 5
  }
];

export const getLessonById = (id: number): Lesson | undefined => {
  return lessons.find(l => l.id === id);
};

export const getLessonsBySaptamana = (saptamana: number): Lesson[] => {
  return lessons.filter(l => l.saptamana === saptamana);
};

export const getNextLesson = (completedLessons: number[]): Lesson | undefined => {
  return lessons.find(l => !completedLessons.includes(l.id));
};
